import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });
axios.defaults.withCredentials = true;

export const Employees = {
  list: () => api.get("/employees").then((r) => r.data),
  get: (id) => api.get(`/employees/${id}`).then((r) => r.data),
  create: (payload) => api.post("/employees", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/employees/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/employees/${id}`).then((r) => r.data),
};

export const Contracts = {
  list: () => api.get("/contracts").then((r) => r.data),
  get: (id) => api.get(`/contracts/${id}`).then((r) => r.data),
  create: (payload) => api.post("/contracts", payload).then((r) => r.data),
  remove: (id) => api.delete(`/contracts/${id}`).then((r) => r.data),
  preview: (id) => api.get(`/contracts/${id}/preview`).then((r) => r.data),
  exportUrl: (id, format) => `${API}/contracts/${id}/export?format=${format}`,
  /**
   * Download PDF/DOCX. Works both in top-level browser context and inside
   * sandboxed iframes (Emergent Preview): opens the export URL in a new tab,
   * where the browser's Content-Disposition: attachment handler triggers the
   * actual download. Cookies flow because it's same-origin.
   */
  download: async (id, format) => {
    const url = `${API}/contracts/${id}/export?format=${format}`;
    const w = window.open(url, "_blank");
    if (w) return; // browser downloads via Content-Disposition, closes the tab
    // Popup blocked — fallback: click a hidden anchor with target=_top
    // which escapes the sandboxed iframe.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_top";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  },
};

export const Meta = {
  get: () => api.get("/meta").then((r) => r.data),
  stats: () => api.get("/stats").then((r) => r.data),
};

export const Fleet = {
  list: () => api.get("/fleet").then((r) => r.data),
  get: (id) => api.get(`/fleet/${id}`).then((r) => r.data),
  create: (payload) => api.post("/fleet", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/fleet/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/fleet/${id}`).then((r) => r.data),
  addRepair: (id, payload) =>
    api.post(`/fleet/${id}/repairs`, payload).then((r) => r.data),
  removeRepair: (id, repairId) =>
    api.delete(`/fleet/${id}/repairs/${repairId}`).then((r) => r.data),
  // GPS + analytics
  locations: () => api.get("/fleet/locations/all").then((r) => r.data),
  alerts: () => api.get("/fleet/alerts/active").then((r) => r.data),
  trips: (id) => api.get(`/fleet/${id}/trips`).then((r) => r.data),
  stats: (id) => api.get(`/fleet/${id}/stats`).then((r) => r.data),
  history: (id) => api.get(`/fleet/${id}/location/history`).then((r) => r.data),
  setGeofence: (id, payload) =>
    api.put(`/fleet/${id}/geofence`, payload).then((r) => r.data),
  clearGeofence: (id) =>
    api.delete(`/fleet/${id}/geofence`).then((r) => r.data),
  // Fuel
  fuelList: (id) => api.get(`/fleet/${id}/fuel`).then((r) => r.data),
  addFuel: (id, payload) =>
    api.post(`/fleet/${id}/fuel`, payload).then((r) => r.data),
  removeFuel: (id, fuelId) =>
    api.delete(`/fleet/${id}/fuel/${fuelId}`).then((r) => r.data),
};

export const Loading = {
  getDraft: () => api.get("/loading/draft").then((r) => r.data),
  saveDraft: (draft) => api.put("/loading/draft", draft).then((r) => r.data),
  preview: () => api.post("/loading/draft/preview").then((r) => r.data),
  dispatch: () => api.post("/loading/draft/dispatch").then((r) => r.data),
  uploadPhoto: (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "paczka.jpg");
    return api.post("/loading/photos", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  photoBlob: (id) => api.get(`/loading/photos/${id}`, { responseType: "blob" }).then((r) => r.data),
};

export const Workspace = {
  search: (q) => api.get("/search", { params: { q } }).then((r) => r.data),
  todo: () => api.get("/todo").then((r) => r.data),
};

export const Routes = {
  parseTemplateText: (text, known_streets = []) => api.post("/routes/templates/parse-text", { text, known_streets }).then((r) => r.data),
  // Templates
  listTemplates: () => api.get("/routes/templates").then((r) => r.data),
  createTemplate: (payload, isPrivate = false) =>
    api.post("/routes/templates", payload, { params: isPrivate ? { private: true } : {} }).then((r) => r.data),
  updateTemplate: (id, payload) =>
    api.put(`/routes/templates/${id}`, payload).then((r) => r.data),
  removeTemplate: (id) =>
    api.delete(`/routes/templates/${id}`).then((r) => r.data),
  duplicateTemplate: (id, isPrivate = false) =>
    api.post(`/routes/templates/${id}/duplicate`, null, { params: isPrivate ? { private: true } : {} }).then((r) => r.data),
  // Templates (need rules list to populate the "assign to rule" dropdown for unmatched stops)
  listTemplate: (id) =>
    api.get(`/routes/templates`).then((r) => r.data.find((t) => t.id === id)),
  assignStop: (planId, finalOrder, payload) =>
    api.put(`/routes/plans/${planId}/stops/${finalOrder}/assign`, payload).then((r) => r.data),
  // Plans
  listPlans: () => api.get("/routes/plans").then((r) => r.data),
  getPlan: (id) => api.get(`/routes/plans/${id}`).then((r) => r.data),
  removePlan: (id) => api.delete(`/routes/plans/${id}`).then((r) => r.data),
  updateStopStatus: (planId, finalOrder, status) =>
    api.put(`/routes/plans/${planId}/stops/${finalOrder}/status`, { status }).then((r) => r.data),
  importPlan: (formData) =>
    api
      .post("/routes/plans/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  resnapPins: (planId) =>
    api.post(`/routes/plans/${planId}/resnap-pins`).then((r) => r.data),
  // Dispatch a plan to a driver (portal-internal, no external courier app)
  dispatchPlan: (planId, driverUserId, vehicleId) =>
    api.post(`/routes/plans/${planId}/dispatch`, {
      driver_user_id: driverUserId,
      vehicle_id: vehicleId,
    }).then((r) => r.data),
  myDeliveries: () => api.get("/routes/my-deliveries").then((r) => r.data),
  listAssignments: (planId) =>
    api.get(`/routes/plans/${planId}/assignments`).then((r) => r.data),
  deleteAssignment: (assignmentId) =>
    api.delete(`/routes/assignments/${assignmentId}`).then((r) => r.data),
  approveAssignment: (assignmentId, payload) =>
    // Accepts a bare number (legacy — sets approved_delivered_count) OR
    // an object with either/both {approved_delivered_count, approved_awizo_count}.
    api.patch(
      `/routes/assignments/${assignmentId}/approve`,
      typeof payload === "number"
        ? { approved_delivered_count: payload }
        : payload,
    ).then((r) => r.data),
  listAllAssignments: (includeCancelled = false) =>
    api.get(`/routes/assignments`, {
      params: includeCancelled ? { include_cancelled: true } : {},
    }).then((r) => r.data),
  cancelAssignment: (assignmentId, reason = "") =>
    api.post(`/routes/assignments/${assignmentId}/cancel`, { reason })
       .then((r) => r.data),
  restoreAssignment: (assignmentId) =>
    api.post(`/routes/assignments/${assignmentId}/restore`).then((r) => r.data),
  settleAssignment: (assignmentId) =>
    api.post(`/routes/assignments/${assignmentId}/settle`).then((r) => r.data),
  listMyWork: () =>
    api.get(`/routes/my-work`).then((r) => r.data),
  listAssignmentPackages: (assignmentId) =>
    api.get(`/routes/assignments/${assignmentId}/packages`).then((r) => r.data),
  updateStopStatus: (planId, finalOrder, status, reason = null, location = null) => {
    const body = { status };
    if (reason) body.reason = reason;
    if (location) body.location = location;
    return api.put(`/routes/plans/${planId}/stops/${finalOrder}/status`, body)
      .then((r) => r.data);
  },
  listDriverRates: () =>
    api.get(`/routes/driver-rates`).then((r) => r.data),
  setDriverRate: (userId, rate) =>
    api.put(`/routes/driver-rates/${userId}`, { rate }).then((r) => r.data),
  // Undelivered stats (admin dashboard)
  getUndeliveredStats: (params = {}) =>
    api.get(`/routes/stats/undelivered-reasons`, { params }).then((r) => r.data),
  // Email contacts (persistent address book for manifest sending)
  listEmailContacts: () =>
    api.get("/routes/email-contacts").then((r) => r.data),
  addEmailContact: (payload) =>
    api.post("/routes/email-contacts", payload).then((r) => r.data),
  removeEmailContact: (contactId) =>
    api.delete(`/routes/email-contacts/${contactId}`).then((r) => r.data),
  sendPlanEmail: (planId, payload) =>
    api.post(`/routes/plans/${planId}/send-email`, payload).then((r) => r.data),
};

export const Finance = {
  paymentReminders: () => api.get("/finance/payment-reminders").then((r) => r.data),
  setPayment: (source, reference, paid) => api.patch(`/finance/payments/${source}/${encodeURIComponent(reference)}`, { paid }).then((r) => r.data),
  meta: () => api.get("/finance/meta").then((r) => r.data),
  summary: () => api.get("/finance/summary").then((r) => r.data),
  kup: () => api.get("/finance/kup").then((r) => r.data),
  list: (params = {}) =>
    api.get("/finance/transactions", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/finance/transactions", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/finance/transactions/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/finance/transactions/${id}`).then((r) => r.data),
  importParse: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/finance/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  importCommit: (rows) =>
    api.post("/finance/import/commit", rows).then((r) => r.data),
};

export const Ksef = {
  environments: () => api.get("/ksef/environments").then((r) => r.data),
  getSettings: () => api.get("/ksef/settings").then((r) => r.data),
  saveSettings: (payload) => api.put("/ksef/settings", payload).then((r) => r.data),
  testConnection: () => api.post("/ksef/test-connection").then((r) => r.data),
  listInvoices: () => api.get("/ksef/invoices").then((r) => r.data),
  getInvoice: (id) => api.get(`/ksef/invoices/${id}`).then((r) => r.data),
  createInvoice: (payload) => api.post("/ksef/invoices", payload).then((r) => r.data),
  deleteInvoice: (id) => api.delete(`/ksef/invoices/${id}`).then((r) => r.data),
  sendInvoice: (id) => api.post(`/ksef/invoices/${id}/send`).then((r) => r.data),
  listContractors: () => api.get("/ksef/contractors").then((r) => r.data),
  saveContractor: (payload) => api.post("/ksef/contractors", payload).then((r) => r.data),
  listBankAccounts: () => api.get("/ksef/bank-accounts").then((r) => r.data),
  saveBankAccount: (payload) => api.post("/ksef/bank-accounts", payload).then((r) => r.data),
  deleteBankAccount: (id) => api.delete(`/ksef/bank-accounts/${id}`).then((r) => r.data),
  syncSalesInvoices: (days = 30) =>
    api.post("/ksef/invoices/sync", null, { params: { days } }).then((r) => r.data),
  listPurchases: () => api.get("/ksef/purchases").then((r) => r.data),
  purchaseCategories: () => api.get("/ksef/purchases/categories").then((r) => r.data),
  classifyPurchases: () => api.post("/ksef/purchases/classify").then((r) => r.data),
  setPurchaseCategory: (number, category) => api.patch(`/ksef/purchases/${encodeURIComponent(number)}/category`, { category }).then((r) => r.data),
  setPurchaseDueDate: (number, due_date) => api.patch(`/ksef/purchases/${encodeURIComponent(number)}/due-date`, { due_date }).then((r) => r.data),
  syncPurchases: (days = 30) =>
    api.post(`/ksef/purchases/sync`, null, { params: { days } }).then((r) => r.data),
  importPurchaseToKup: (ksefNumber, category = "Inne") =>
    api
      .post(`/ksef/purchases/${encodeURIComponent(ksefNumber)}/import-to-kup`, null, {
        params: { category },
      })
      .then((r) => r.data),
  deletePurchase: (ksefNumber) =>
    api.delete(`/ksef/purchases/${encodeURIComponent(ksefNumber)}`).then((r) => r.data),
};

export const Courier = {
  health: () => api.get("/courier/health").then((r) => r.data),
  summary: () => api.get("/courier/summary").then((r) => r.data),
  listRoutes: () => api.get("/courier/routes").then((r) => r.data),
  getRoute: (id) => api.get(`/courier/routes/${id}`).then((r) => r.data),
  getStop: (routeId, stopId) =>
    api.get(`/courier/routes/${routeId}/stops/${stopId}`).then((r) => r.data),
  removeRoute: (id) => api.delete(`/courier/routes/${id}`).then((r) => r.data),
  sendPlan: (planId, assignedUserId, vehicleId) => {
    const params = {};
    if (assignedUserId) params.assigned_user_id = assignedUserId;
    if (vehicleId) params.vehicle_id = vehicleId;
    return api.post(`/courier/send-plan/${planId}`, null, { params }).then((r) => r.data);
  },
  uploadManifest: (file, name) => {
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    return api.post("/courier/upload-manifest", fd).then((r) => r.data);
  },
  getTrack: (routeId, since) =>
    api.get(`/courier/routes/${routeId}/track`, { params: since ? { since } : {} }).then((r) => r.data),
  getLatestLocation: (routeId) =>
    api.get(`/courier/routes/${routeId}/track/latest`).then((r) => r.data),
  listCouriers: () => api.get("/courier/couriers").then((r) => r.data),
  myRoutes: () => api.get("/courier/my-routes").then((r) => r.data),
};

export const Schedule = {
  list: (params = {}) =>
    api.get("/schedule", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/schedule", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/schedule/${id}`, payload).then((r) => r.data),
  approve: (id, review_notes = "") =>
    api.put(`/schedule/${id}/approve`, { review_notes }).then((r) => r.data),
  reject: (id, review_notes = "") =>
    api.put(`/schedule/${id}/reject`, { review_notes }).then((r) => r.data),
  remove: (id) => api.delete(`/schedule/${id}`).then((r) => r.data),
  couriers: () => api.get("/schedule/couriers").then((r) => r.data),
};

export const Notifications = {
  list: () => api.get("/notifications").then((r) => r.data),
};

export const Gamification = {
  me: () => api.get("/gamification/me").then((r) => r.data),
  achievements: () => api.get("/gamification/achievements").then((r) => r.data),
  shop: () => api.get("/gamification/shop").then((r) => r.data),
  myOrders: () => api.get("/gamification/orders").then((r) => r.data),
  placeOrder: (item_id, note = "") =>
    api.post("/gamification/shop/order", { item_id, note }).then((r) => r.data),
};

export const GamificationAdmin = {
  listAchievements: () => api.get("/admin/gamification/achievements").then((r) => r.data),
  createAchievement: (p) => api.post("/admin/gamification/achievements", p).then((r) => r.data),
  updateAchievement: (code, p) => api.put(`/admin/gamification/achievements/${code}`, p).then((r) => r.data),
  deleteAchievement: (code) => api.delete(`/admin/gamification/achievements/${code}`).then((r) => r.data),
  listShop: () => api.get("/admin/gamification/shop").then((r) => r.data),
  createItem: (p) => api.post("/admin/gamification/shop", p).then((r) => r.data),
  updateItem: (id, p) => api.put(`/admin/gamification/shop/${id}`, p).then((r) => r.data),
  deleteItem: (id) => api.delete(`/admin/gamification/shop/${id}`).then((r) => r.data),
  listOrders: (status) =>
    api.get("/admin/gamification/orders", { params: status ? { status } : {} }).then((r) => r.data),
  approveOrder: (id, note = "") =>
    api.post(`/admin/gamification/orders/${id}/approve`, { note }).then((r) => r.data),
  rejectOrder: (id, note = "") =>
    api.post(`/admin/gamification/orders/${id}/reject`, { note }).then((r) => r.data),
  deliverOrder: (id, note = "") =>
    api.post(`/admin/gamification/orders/${id}/deliver`, { note }).then((r) => r.data),
  grantDiamonds: (user_id, amount, reason = "") =>
    api.post("/admin/gamification/grant-diamonds", { user_id, amount, reason }).then((r) => r.data),
  grantAchievement: (user_id, code) =>
    api.post("/admin/gamification/grant-achievement", { user_id, code }).then((r) => r.data),
  stats: () => api.get("/admin/gamification/stats").then((r) => r.data),
  users: () => api.get("/admin/gamification/users").then((r) => r.data),
  getConfig: () => api.get("/admin/gamification/config").then((r) => r.data),
  updateConfig: (payload) => api.put("/admin/gamification/config", payload).then((r) => r.data),
  resetConfig: () => api.post("/admin/gamification/config/reset").then((r) => r.data),
  listBoosts: () => api.get("/admin/gamification/boosts").then((r) => r.data),
  createBoost: (p) => api.post("/admin/gamification/boosts", p).then((r) => r.data),
  updateBoost: (id, p) => api.put(`/admin/gamification/boosts/${id}`, p).then((r) => r.data),
  deleteBoost: (id) => api.delete(`/admin/gamification/boosts/${id}`).then((r) => r.data),
  toggleBoost: (id) => api.post(`/admin/gamification/boosts/${id}/toggle`).then((r) => r.data),
};



export const Consents = {
  // User-facing
  pending: () => api.get("/consents/pending").then((r) => r.data),
  accept: (document_ids) =>
    api.post("/consents/accept", { document_ids }).then((r) => r.data),
  mine: () => api.get("/consents/mine").then((r) => r.data),
  // Admin
  adminList: () => api.get("/admin/consents").then((r) => r.data),
  adminKinds: () => api.get("/admin/consents/kinds").then((r) => r.data),
  adminPublish: (payload) =>
    api.post("/admin/consents", payload).then((r) => r.data),
  adminEdit: (id, payload) =>
    api.patch(`/admin/consents/${id}`, payload).then((r) => r.data),
  adminDelete: (id) =>
    api.delete(`/admin/consents/${id}`).then((r) => r.data),
  adminAcceptances: (id) =>
    api.get(`/admin/consents/${id}/acceptances`).then((r) => r.data),
};

export const Broadcasts = {
  // User
  pending: () => api.get("/broadcasts/pending").then((r) => r.data),
  ack: (id) => api.post(`/broadcasts/${id}/ack`).then((r) => r.data),
  mine: () => api.get("/broadcasts/mine").then((r) => r.data),
  // Admin
  adminList: () => api.get("/admin/broadcasts").then((r) => r.data),
  adminSend: (payload) => api.post("/admin/broadcasts", payload).then((r) => r.data),
  adminDetail: (id) => api.get(`/admin/broadcasts/${id}`).then((r) => r.data),
  adminDelete: (id) => api.delete(`/admin/broadcasts/${id}`).then((r) => r.data),
};

// Admin: list users for recipient picker (already exposed under /users but
// wrap here for convenience in the broadcast composer).
export const UsersAPI = {
  list: () => api.get("/users").then((r) => r.data),
};
